import axios from 'axios';
import store from 'store';

import Services from './index.service';

import buildQuery from 'odata-query';
import { isValid, parseISO } from 'date-fns';

import { ITicketsData, ITickets, IRequestFilter, ScheduleTicketProps } from 'models/Ticket';
import { IRequestConfig, MethodsTypes } from 'models/Axios';
import { ISignUpProtocol } from 'pages/Login/SignUpProtocol/interface';

import toast from 'components/Toast/dispatch';
import { FormSimpleProps } from 'components/SimpleForm/interface';
import { SetMessageErrorNewProtocol } from 'utils/OpenProtocol';
import { FormLessReviewProps } from 'pages/ReviewOrderLost/interface';
import { AdverseEventRegistrationPayload } from 'pages/AdverseEventsForm/interface';

export default class TicketServices extends Services<ITicketsData, ITickets> {
  /**
   * Function to get a limited qtd of items
   * @param currentPage Range to skip, you need only pass currentPage (ex: currentPage = 1)
   * @param pageSize Range to take, you need only pass pageSize (ex: pageSize = 15)
   */
  get = (
    currentPage?: number,
    pageSize?: number,
    orderby?: string,
    filter?: {}
  ) => {
    const { customer } = store.getState();
    const { user } = store.getState();
    const skip = currentPage || 0;
    const top = pageSize || 20;
    const order = orderby ? `${orderby}` : undefined;
    const filterQuery = buildQuery({
      filter: {
        ...filter,
        customerId: customer?.id || user?.selectedCustomerId,
      },
    });

    const config: IRequestConfig<ITickets> = {
      url: `/ticket${filterQuery}`,
      params: {
        $top: `${top}`,
        $skip: `${skip * top}`,
        $orderby: order,
      },
    };

    return this.httpRequest(config);
  };

  /**
   * Function to create new ticket
   * @param data
   */
  create = async (data: ITickets | FormSimpleProps) => {

    const config: IRequestConfig<ITickets> = {
      url: `${this.base_url}/ticket`,
      method: MethodsTypes.post,
      data,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.access_token()}`,
      },
    };

    axios.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        return error.response;
      }
    );

    return await axios(config).then((result) => result);
  };

  /**
   * Function to create new ticket (AllowAnonymous)
   * @param data
   */
  registerCustomerAnonymous = (data: any) => {
    const config: IRequestConfig<ISignUpProtocol> = {
      url: `${this.base_url}/ticket/register-customer-anonymous`,
      method: MethodsTypes.post,
      data,
      headers: {
        'Content-Type': 'application/json',
      },
    };

    return axios(config);
  };

  /**
   * Function to update a ticket
   * @param data
   */
  update = async (data: any) => {
    const config: IRequestConfig<ITickets> = {
      url: `${this.base_url}/ticket`,
      method: MethodsTypes.put,
      data,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.access_token()}`,
      },
    };

    axios.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        return error.response;
      }
    );

    return await axios(config).then((result) => result);
  };

  rating = (data: any) => {
    const config: IRequestConfig<ITickets> = {
      url: `${this.base_url}/ticket/rating`,
      method: MethodsTypes.post,
      data,
      headers: {
        'Content-Type': 'application/json',
      },
    };

    return axios(config);
  };

  updateFields = (data: any) => {
    const config: IRequestConfig<ITickets> = {
      url: `${this.base_url}/ticket/update_fields`,
      method: MethodsTypes.put,
      data,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.access_token()}`,
      },
    };

    return axios(config);
  };

  getCategoriesReasons = () => {
    const config: IRequestConfig<ITickets> = {
      url: `${this.base_url}/categories/reasons/classifications`,
      method: MethodsTypes.get,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.access_token()}`,
      },
    };

    return axios(config);
  };

  filterProtocols = (data: IRequestFilter) => {
    const {
      protocolNumber, startPeriod, lastPeriod, keyword, bus, categories,
      reasons, responsibleGroups, subsidiaryIds, phaseStatus, brands,
      statusType, currentPage, pageSize, customerIds } = data;

    const dateStart = startPeriod ? parseISO(startPeriod) : '';
    const dateEnd = lastPeriod ? parseISO(lastPeriod) : '';
    const startDateTime = isValid(dateStart) ? `${startPeriod}` : '';
    const endDateTime = isValid(dateEnd) ? `${lastPeriod}` : '';

    const skip = currentPage === 0 ? currentPage : Number(currentPage) * 10;
    const top = pageSize || 10;

    const sortBy = data.sortBy ? `${data.sortBy}` : undefined;
    const sortDirection = data.sortDirection ? `${data.sortDirection}` : undefined;

    const params: Record<string, string | number | undefined> = {
      protocolNumber,
      startPeriod: startDateTime,
      lastPeriod: endDateTime,
      keyword,
      bus: bus?.join(','),
      categories: categories?.join(','),
      reasons: reasons?.join(','),
      responsibleGroups: responsibleGroups?.join(','),
      subsidiaryIds: subsidiaryIds?.join(','),
      phaseStatus: phaseStatus?.join(','),
      brands: brands?.join(','),
      statusType: statusType?.join(','),
      skip,
      top,
      sortBy,
      sortDirection,
      customerIds: customerIds || undefined,
    };

    const queryString = Object.keys(params)
      .filter((key) => params[key] !== undefined && params[key] !== '')
      .map((key) => `${key}=${params[key] as string}`)
      .join('&');

    const config: IRequestConfig<ITickets> = {
      url: `${this.base_url}/ticket/filters?${queryString}`,
      method: MethodsTypes.get,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.access_token()}`,
      },
    };

    return axios(config);
  };

  public async downloadData(data: IRequestFilter) {
    const {
      protocolNumber, startPeriod, lastPeriod, keyword, bus, categories,
      reasons, responsibleGroups, subsidiaryIds, phaseStatus, brands,
      statusType, protocolOrigin, customerIds } = data;

    const dateStart = startPeriod ? parseISO(startPeriod) : '';
    const dateEnd = lastPeriod ? parseISO(lastPeriod) : '';
    const startDateTime = isValid(dateStart) ? `${startPeriod}T00:00:00.000Z` : '';
    const endDateTime = isValid(dateEnd) ? `${lastPeriod}T23:59:59.999Z` : '';

    const sortBy = data.sortBy ? `${data.sortBy}` : undefined;
    const sortDirection = data.sortDirection ? `${data.sortDirection}` : undefined;
    const protocolOriginFilter = protocolOrigin ?? [];
    const customerIdsFilter = customerIds ?? [];

    const config: IRequestConfig<ITickets> = {
      url: `/ticket/export-ticket-excel?protocolNumber=${protocolNumber}&startPeriod=${startDateTime}
      &lastPeriod=${endDateTime}&keyword=${keyword}&bus=${bus}&categories=${categories}&reasons=${reasons}
      &responsibleGroups=${responsibleGroups}&subsidiaryIds=${subsidiaryIds}&phaseStatus=${phaseStatus}
      &brands=${brands}&statusType=${statusType}&sortBy=${sortBy}&sortDirection=${sortDirection}
      &protocolOrigin=${protocolOriginFilter}&customerIds=${customerIdsFilter}`,
      method: MethodsTypes.get,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.access_token()}`,
      },
      timeout: 300000
    };

    return await this.httpRequest(config)
      .then(result => {
        result.data ?
          TicketServices.CreateDownloadLink(result.data.fileUrl) :
          toast.error('Desculpe, erro inesperado!');
      })
      .catch(() => toast.error('Desculpe, erro inesperado!'));
  }

  correctionLetter = async (orderId: string, description: string, setError: Function) => {
    const config: IRequestConfig<ITickets> = {
      url: `${this.base_url}/ticket/correction-letter`,
      method: MethodsTypes.post,
      data: {
        orderId: orderId,
        description: description
      },
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.access_token()}`,
      },

    };

    axios.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        return error.response;
      }
    );

    const request = await axios(config)
      .then((result) => result);

    if (request.status !== 200) {
      SetMessageErrorNewProtocol(setError, request.data.data);
      return false;
    }

    return request;
  };

  public static CreateDownloadLink = (path: string | undefined) => {
    if (path) {
      const link = document.createElement('a');
      link.href = path;
      link.download = 'download';
      link.click();
    }
  };

  registerScheduleTicket = (data: ScheduleTicketProps, protocolIdParams: string) => {
    const config: IRequestConfig<ScheduleTicketProps> = {
      url: `${this.base_url}/ticket/schedule-ticket`,
      method: MethodsTypes.post,
      data,
      headers: {
        "Content-Type": 'application/json',
        "token": protocolIdParams
      }
    };

    return axios(config);
  };

  public async getDevolutionTicketApproval(id: string, phase: string) {
    try {
      const token = this.access_token();
      const response = await axios.get(
        `${this.base_url}/ticket/devolution-ticket-approval/${id}?phase=${phase}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return { success: true, data: response.data };
    } catch (error) {
      const errorMsg = axios.isAxiosError(error) && error.response?.data?.message
        ? error.response.data.message
        : "Ocorreu um erro ao buscar o protocolo";
      return { success: false, message: errorMsg };
    }
  }

  saveDevolutionTicketApprovalData = async (data: any) => {
    try {
      const response = await axios.put(
        `${this.base_url}/ticket/devolution-ticket-approval`,
        data,
        {
          headers: { Authorization: `Bearer ${this.access_token()}` },
        }
      );
      return { success: true, data: response.data };
    } catch (error) {
      let errorMsg = "Ocorreu um erro na requisição";

      if (axios.isAxiosError(error)) {
        errorMsg = error.response?.data?.message || errorMsg;
      }

      return { success: false, message: errorMsg };
    }
  };

  getTicketAdverseEvent = async (cardId: string) => {
    const config: IRequestConfig<any> = {
      url: `${this.base_url}/ticket/adverse-event/${cardId}`,
      method: MethodsTypes.get,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.access_token()}`,
      },
    };

    return axios(config);
  };

  getTicketLossReview = async (id: string) => {
    const config: IRequestConfig<any> = {
      url: `${this.base_url}/ticket/loss-review/${id}`,
      method: MethodsTypes.get,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.access_token()}`,
      },
    };

    return axios(config);
  };

  putTicketLossReview = (data: FormLessReviewProps) => {
    const config: IRequestConfig<any> = {
      url: `${this.base_url}/ticket/loss-review`,
      method: MethodsTypes.put,
      data,
      headers: {
        "Content-Type": 'application/json',
        Authorization: `Bearer ${this.access_token()}`
      }
    };

    return axios(config);
  };

  sendAdverseEvent = (data: AdverseEventRegistrationPayload) => {
    const config: IRequestConfig<any> = {
      url: `${this.base_url}/ticket/adverse-event`,
      method: MethodsTypes.put,
      data,
      headers: {
        "Content-Type": 'application/json',
        Authorization: `Bearer ${this.access_token()}`
      }
    };

    return axios(config);
  };
}
